import React from 'react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/glyph/cross';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, { ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { useIntl } from '@atlassian/jira-intl';
import {
	PRIORITY_SCHEME_MODAL,
	PRIORITY_SCHEME_MODAL_SUBMIT_BUTTON_SELECTOR,
} from '../../../common/constants';
import { DEFAULT, SAVING } from '../../../common/ui/constants';
import { type PrioritySchemeFormState, ModalOnCompleteStatus } from '../../../common/ui/types';
import messages from './messages';

export type ModalViewProps = {
	renderPrimaryButton?: () => React.ReactElement;
	prioritySchemeFormStatus: PrioritySchemeFormState;
	/**
	 * Callback
	 */
	onSubmit: () => void;
	/**
	 * Callback
	 */
	onClose: (status: (typeof ModalOnCompleteStatus)[keyof typeof ModalOnCompleteStatus]) => void;
	isCreatingNewScheme: boolean;
	/**
	 * 0 indexed
	 */
	tabIndex: number;
	/**
	 * 0 Indexed
	 * @param index
	 */
	setTabIndex: (index: number) => void;
	renderDetailsContent: () => React.ReactElement;
	renderProjectsContent: () => React.ReactElement;
};

export const ModalView = ({
	renderPrimaryButton,
	prioritySchemeFormStatus,
	onSubmit,
	onClose,
	isCreatingNewScheme,
	renderDetailsContent,
	renderProjectsContent,
	tabIndex,
	setTabIndex,
}: ModalViewProps) => {
	const { formatMessage } = useIntl();

	const renderSubmitButtonTitle: string = isCreatingNewScheme
		? formatMessage(messages.addPrioritySchemeButtonTitle)
		: formatMessage(messages.editPrioritySchemeButtonTitle);

	const isDisabled: boolean = prioritySchemeFormStatus !== DEFAULT;

	return (
		<Modal testId={PRIORITY_SCHEME_MODAL} onClose={() => onClose(ModalOnCompleteStatus.DISMISSAL)}>
			<ModalHeader>
				<ModalTitle>
					{isCreatingNewScheme
						? formatMessage(messages.addPrioritySchemeTitle)
						: formatMessage(messages.editPrioritySchemeTitle)}
				</ModalTitle>
				<IconButton
					onClick={() => onClose(ModalOnCompleteStatus.DISMISSAL)}
					icon={CrossIcon}
					label={formatMessage(messages.closeModalButtonLabel)}
				/>
			</ModalHeader>
			<ModalBody>
				<Tabs id="priority-scheme-modal-tabs" selected={tabIndex} onChange={setTabIndex}>
					<TabList>
						<Tab>{formatMessage(messages.detailsPrioritySchemeTab)}</Tab>
						<Tab>{formatMessage(messages.projectsPrioritySchemeTab)}</Tab>
					</TabList>
					<TabPanel>{renderDetailsContent()}</TabPanel>
					<TabPanel>{renderProjectsContent()}</TabPanel>
				</Tabs>
			</ModalBody>
			<ModalFooter>
				<Button
					appearance="link"
					onClick={() => onClose(ModalOnCompleteStatus.DISMISSAL)}
					isDisabled={isDisabled}
				>
					{formatMessage(messages.cancelPrioritySchemeButtonTitle)}
				</Button>
				{renderPrimaryButton ? (
					renderPrimaryButton()
				) : (
					<Button
						testId={PRIORITY_SCHEME_MODAL_SUBMIT_BUTTON_SELECTOR}
						appearance="primary"
						onClick={onSubmit}
						type="submit"
						isDisabled={isDisabled}
					>
						{prioritySchemeFormStatus === SAVING ? (
							<Spinner size="small" />
						) : (
							renderSubmitButtonTitle
						)}
					</Button>
				)}
			</ModalFooter>
		</Modal>
	);
};
